import React, { useMemo, useState } from 'react';
import Helpers from 'helpers/Helpers';
import {
  buildThuNoXacNhan,
  cheDoToMode,
  sumThem,
  TCheDoThuNo,
  THU_NO_EDIT_KEYS
} from '../common/Hooks/hook/thuNoAllocation';

// ===== PHIẾU XÁC NHẬN THU NỢ — bố cục 4 mục, đọc từ trên xuống =====
//   Kết luận     : khớp hay có vấn đề (1 dải)
//   1. Đối chiếu tiền : [Tiền khách đưa] → [Trả cho đơn] → [Tiền dư để lại cho khách]   (1 khung)
//   2. Chi tiết theo khoản : 6 khoản + phép kiểm Tổng chi tiết − Tổng gạch nợ = Chênh lệch
//   3. Danh sách đơn
// Chữ đậm chỉ dùng cho: tiêu đề mục, dòng tổng, kết quả cuối. Dữ liệu thường để chữ thường.

const TEN_O: Record<string, string> = {
  thuCongNoThietBi: 'Tiền SP',
  thuVat: 'VAT',
  thuChenhLech: 'Chênh lệch',
  thuGoiCuoc: 'Gói cước',
  tienPhi: 'Phí',
  tienCong: 'Tiền công'
};

const NGUONG_TIM_KIEM = 8; // từ ngần này đơn trả đủ trở lên mới hiện tìm / lọc

// Bề ngang tối đa của nội dung phiếu. Modal rộng hơn thì nội dung vẫn gọn giữa, không bị kéo giãn.
const BE_NGANG_TOI_DA = 1180;

// Chiều cao cố định để các dòng dính (sticky) xếp chồng đúng. Đổi ở đây thì đổi class tương ứng:
//   header = h-[28px] / top-[28px]  |  dòng Tổng cộng = h-[32px] / bottom-[32px]  |  dòng đang lọc = h-[24px]
const C = {
  ink: '#0f172a',
  chu2: '#1e293b', // trước: #475569, nhãn (Tiền SP, VAT, Thực nhận...)
  chu3: '#64748b', // trước: #94a3b8, dấu + − =, số thứ tự, dấu "–"
  nen: '#f8fafc',
  vien: '#e2e8f0',
  vienNhat: '#f1f5f9',
  xanhChu: '#1d4ed8',
  xanhNen: '#eff6ff',
  luc: '#047857',
  lucNen: '#ecfdf5',
  cam: '#b45309',
  camNen: '#fffbeb',
  camBadge: '#d97706',
  do: '#b91c1c',
  doNen: '#fef2f2'
};

// Vạch kẻ đậm duy nhất của bảng đơn: nằm trên dòng Tổng cộng (quy ước sổ kế toán)
const VIEN_TONG = '1px solid #334155';

const tien = (v: number) => Helpers.FormatMoney(Number(v || 0), true);

// ---------- Mục: tiêu đề nằm NGOÀI khung, không còn thanh xám trên từng khối ----------
const Muc = ({
  tieuDe,
  phai,
  children
}: {
  tieuDe: React.ReactNode;
  phai?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <section>
    <div className="mb-1 flex items-center justify-between gap-2">
      <h4 className="m-0 text-[13px] font-semibold" style={{ color: C.ink }}>
        {tieuDe}
      </h4>
      {phai}
    </div>
    {children}
  </section>
);

// ---------- 1 cột của khung đối chiếu ----------
const Cot = ({ tieuDe, children }: { tieuDe: string; children: React.ReactNode }) => (
  <div className="min-w-0 px-3 py-2">
    <div className="mb-1 text-[13px] font-semibold" style={{ color: C.chu2 }}>
      {tieuDe}
    </div>
    {children}
  </div>
);

// Dấu − và = nối 3 cột thành 1 phương trình
const Toan = ({ k }: { k: string }) => (
  <div className="hidden select-none items-center px-1 text-[20px] font-light lg:flex" style={{}}>
    {k}
  </div>
);

// ---------- 1 dòng sổ dọc: [dấu] nhãn ........ số ----------
const Dong = ({
  dau,
  nhan,
  val,
  tong,
  mau
}: {
  dau?: '+' | '−' | '=';
  nhan: string;
  val: number | string;
  tong?: boolean; // dòng cộng: đậm vừa + kẻ trên
  mau?: string; // chỉ dùng cho kết quả cần chú ý
}) => (
  <div
    className={`flex items-baseline justify-between gap-3 py-[2px] ${tong ? 'mt-1 border-t pt-1.5' : ''}`}
    style={tong ? { borderColor: '#334155' } : undefined}
  >
    <span className={`min-w-0 truncate font-semibold`} style={mau ? { color: mau } : undefined}>
      <span className="inline-block w-4 select-none" style={{}}>
        {dau || ''}
      </span>
      {nhan}
    </span>
    <span className={`shrink-0 tabular-nums ${tong ? 'font-semibold' : ''}`} style={mau ? { color: mau } : undefined}>
      {typeof val === 'number' ? tien(val) : val}
    </span>
  </div>
);

// ---------- Nhãn đơn trả một phần ----------
const NhanMotPhan = () => (
  <span className="ml-2 align-middle" style={{ position: 'relative', color: 'blue', fontWeight: 'bold' }}>
    (Một phần)
  </span>
);

const TH = 'sticky top-0 z-20 h-[28px] px-3 font-medium whitespace-nowrap bg-[#f8fafc] text-[#1e293b]';
const TD = 'px-3 py-[3px] whitespace-nowrap';
const NUM = 'text-right tabular-nums';

type IThuNoXacNhan = {
  data: ReturnType<typeof buildThuNoXacNhan>;
  khachHang?: string;
  maKhachHang?: string;
  soDienThoai?: string;
  hinhThucThanhToan?: string;
  noiDungChuyenKhoan?: string;
  cheDo?: TCheDoThuNo;
};

export const ThuNoXacNhan = ({
  data,
  khachHang,
  maKhachHang,
  soDienThoai,
  hinhThucThanhToan,
  noiDungChuyenKhoan,
  cheDo = 'auto'
}: IThuNoXacNhan) => {
  const phanBo = data.phanBo;
  const tienPhanBo = Number(phanBo?.tien || 0);

  // Tổng gạch nợ = tiền nợ của các đơn trả ĐỦ + số tiền đã phân bổ cho đơn trả MỘT PHẦN
  const tongGachNo = data.tongDonThanhToan + tienPhanBo;

  // Cộng dọc: nợ = nợ các đơn trả đủ + tổng nợ đơn một phần; thanh toán = tổng gạch nợ; còn nợ = phần chưa trả
  const congTienNo = data.tongDonThanhToan + Number(phanBo?.tongNo || 0);
  const congConNo = Number(phanBo?.noConLai || 0);
  const soDon = data.donThanhToan.length + (phanBo ? 1 : 0);

  const tongChiTiet = sumThem(data.chiTiet);
  const lech = tongChiTiet - tongGachNo;
  const thieu = Number(data.thieu || 0);

  // ----- Kết luận: gom mọi vấn đề lên đầu phiếu để kế toán biết ngay có nên xác nhận không -----
  const vanDe: { muc: 'do' | 'cam'; text: string }[] = [];
  if (thieu > 0) {
    vanDe.push({ muc: 'do', text: `Thu thiếu ${tien(thieu)} đ: tổng chi tiết / công nợ vượt tiền thanh toán` });
  }
  if (lech !== 0) {
    vanDe.push({
      muc: 'cam',
      text: `Tổng chi tiết ${tien(tongChiTiet)} lệch ${tien(Math.abs(lech))} đ so với tổng gạch nợ ${tien(tongGachNo)}`
    });
  }
  const coLoiDo = vanDe.some(v => v.muc === 'do');

  // ----- tìm + lọc (chỉ áp cho đơn trả đủ; đơn một phần luôn hiện) -----
  const [tuKhoa, setTuKhoa] = useState('');
  const [loaiChon, setLoaiChon] = useState('');

  const dsLoai = useMemo(() => {
    const dem: Record<string, number> = {};
    data.donThanhToan.forEach(d => {
      dem[d.loai] = (dem[d.loai] || 0) + 1;
    });
    return Object.keys(dem).map(loai => ({ loai, soLuong: dem[loai] }));
  }, [data.donThanhToan]);

  const dsHienThi = useMemo(() => {
    const kw = tuKhoa.trim().toLowerCase();
    return data.donThanhToan.filter(
      d => (!loaiChon || d.loai === loaiChon) && (!kw || String(d.maDonHang).toLowerCase().includes(kw))
    );
  }, [data.donThanhToan, tuKhoa, loaiChon]);

  const dangLoc = !!tuKhoa.trim() || !!loaiChon;
  const tongHienThi = useMemo(() => dsHienThi.reduce((s, d) => s + Number(d.tienNo || 0), 0), [dsHienThi]);
  const hienBoLoc = data.donThanhToan.length >= NGUONG_TIM_KIEM;

  const xoaLoc = () => {
    setTuKhoa('');
    setLoaiChon('');
  };

  const chip = (active: boolean) =>
    `rounded-full px-2.5 py-0.5 ${
      active ? 'bg-[#dbeafe] font-medium text-[#1e40af]' : 'bg-[#f1f5f9] text-[#334155] hover:bg-[#e2e8f0]'
    }`;

  const FOOT = 'sticky z-10 px-3';

  return (
    <div className="mx-auto flex w-full flex-col gap-3 text-[13px]" style={{ color: C.ink, maxWidth: BE_NGANG_TOI_DA }}>
      {/* ===== Thông tin khách ===== */}
      {(khachHang || hinhThucThanhToan) && (
        <div className="grid grid-cols-1 gap-x-6 gap-y-1 border-b pb-2 sm:grid-cols-3" style={{ borderColor: C.vien }}>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide" style={{}}>
              Khách hàng
            </div>
            <div className="truncate font-semibold" style={{ color: C.ink }}>
              {khachHang}
              {maKhachHang && (
                <span className="ml-1.5 font-normal" style={{ color: C.chu2 }}>
                  · {maKhachHang}
                </span>
              )}
            </div>
            {soDienThoai && <div style={{ color: C.chu2 }}>{soDienThoai}</div>}
          </div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide" style={{}}>
              Hình thức thanh toán
            </div>
            <div className="font-semibold" style={{ color: C.do }}>
              {hinhThucThanhToan}
            </div>
            {noiDungChuyenKhoan && (
              <div className="truncate" style={{ color: C.chu2 }}>
                ND CK: {noiDungChuyenKhoan}
              </div>
            )}
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide" style={{}}>
              Chế độ thu
            </div>
            <span
              className="inline-block rounded-full px-2.5 py-0.5 text-[12px] font-semibold whitespace-nowrap"
              style={
                cheDo === 'custom'
                  ? { background: '#fde68a', color: '#92400e' }
                  : { background: '#dbeafe', color: '#1e40af' }
              }
            >
              {cheDo === 'custom' ? 'Tùy chỉnh' : 'Tự động'}
            </span>
          </div>
        </div>
      )}

      {/* ===== KẾT LUẬN: khớp hay có vấn đề ===== */}
      {/* {vanDe.length === 0 ? (
        <div
          className="flex items-center gap-2 rounded-md px-3 py-1 font-medium"
          style={{ background: C.lucNen, color: C.luc }}
        >
          <span aria-hidden>✓</span>
          Số liệu khớp: tổng chi tiết bằng tổng gạch nợ, không thu thiếu
        </div>
      ) : (
        <div
          className="rounded-md px-3 py-1 font-medium"
          style={{ background: coLoiDo ? C.doNen : C.camNen, color: coLoiDo ? C.do : C.cam }}
          role="alert"
        >
          {vanDe.map((v, i) => (
            <div key={i} className="flex items-start gap-2">
              <span aria-hidden>{v.muc === 'do' ? '✕' : '⚠'}</span>
              <span>{v.text}</span>
            </div>
          ))}
        </div>
      )} */}

      {/* ===== 1 + 2: đối chiếu tiền (trái) và chi tiết theo khoản (phải) ===== */}
      {/* ----- 1. LUỒNG TIỀN: khách đưa → trả cho đơn → dư để lại ----- */}
      <Muc tieuDe="1. Luồng tiền">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-stretch">
          <div className="min-w-0 flex-1 rounded-md border px-3 py-2" style={{ borderColor: C.vien }}>
            <div className="text-[11px] uppercase tracking-wide" style={{}}>
              Tiền thực nhận
            </div>
            <div className="text-[18px] font-bold tabular-nums">{tien(data.tienThucNhan)}</div>
          </div>
          <div className="hidden items-center text-[20px] lg:flex" style={{}}>
            →
          </div>
          <div
            className="min-w-0 flex-1 rounded-md border px-3 py-2"
            style={{ borderColor: '#bfdbfe', background: C.xanhNen }}
          >
            <div className="text-[11px] uppercase tracking-wide" style={{}}>
              Tiền đơn nợ
            </div>
            <div className="text-[18px] font-bold tabular-nums" style={{ color: C.xanhChu }}>
              {tien(tongGachNo)}
            </div>
          </div>
          <div className="hidden items-center text-[20px] lg:flex" style={{}}>
            →
          </div>
          <div
            className="min-w-0 flex-1 rounded-md border px-3 py-2"
            style={{ borderColor: `${C.luc}55`, background: C.lucNen }}
          >
            <div className="text-[11px] uppercase tracking-wide" style={{}}>
              Tiền dư
            </div>
            <div className="text-[18px] font-bold tabular-nums" style={{ color: C.luc }}>
              {tien(data.duNoConLai)}
            </div>
          </div>
        </div>
        {(data.duNoDung > 0 || data.phuPhi > 0) && (
          <div className="mt-1.5 text-[12px]" style={{ color: C.chu2 }}>
            Khách đưa {tien(data.tienThucNhan)}
            {data.duNoDung > 0 && <> + dư nợ dùng {tien(data.duNoDung)}</>}
            {data.phuPhi > 0 && <> − phụ phí {tien(data.phuPhi)}</>}
          </div>
        )}
      </Muc>

      {/* ----- 2. CHI TIẾT 6 KHOẢN ----- */}
      <Muc tieuDe="2. Chi tiết">
        <div className="rounded-md border bg-white px-2 py-1" style={{ borderColor: C.vien }}>
          {/* Nhãn trái, số phải, 2 cột */}
          <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-3">
            {THU_NO_EDIT_KEYS.map(key => (
              <div
                key={key}
                className="flex min-w-0 items-baseline justify-between gap-2 border-b border-dashed py-0.5"
                style={{ borderColor: C.vienNhat }}
              >
                <span className="min-w-0 truncate" style={{}} title={TEN_O[key]}>
                  {TEN_O[key]}
                </span>
                <span className="shrink-0 font-semibold tabular-nums" style={{ color: C.chu2 }}>
                  {tien(data.chiTiet[key])}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Chỉ hiện khi chi tiết không khớp số đã trả. Khớp thì không có dòng nào. */}
        {/* {lech !== 0 && (
          <div className="mt-1.5 rounded-md px-2 py-1.5" style={{ background: `${C.cam}14`, color: C.cam }}>
            Chi tiết {tien(tongChiTiet)}, trả cho đơn {tien(tongGachNo)}: chênh {tien(Math.abs(lech))} đ.
            {lech > 0
              ? ' Phần chênh nằm trong tiền dư để lại cho khách.'
              : ' Chi tiết thấp hơn số đã trả, kiểm lại các ô.'}
          </div>
        )} */}
      </Muc>

      {/* ===== 3. DANH SÁCH ĐƠN ===== */}
      <section>
        <div className="mb-1 flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5">
          <h4 className="m-0 text-[13px] font-semibold">3. Danh sách đơn </h4>

          {hienBoLoc && (
            <div className="flex flex-wrap items-center gap-1.5">
              {dsLoai.length > 1 && (
                <>
                  <button type="button" onClick={() => setLoaiChon('')} className={chip(!loaiChon)}>
                    Tất cả {data.donThanhToan.length}
                  </button>
                  {dsLoai.map(l => (
                    <button
                      key={l.loai}
                      type="button"
                      onClick={() => setLoaiChon(loaiChon === l.loai ? '' : l.loai)}
                      className={chip(loaiChon === l.loai)}
                    >
                      {l.loai} {l.soLuong}
                    </button>
                  ))}
                </>
              )}
              <input
                value={tuKhoa}
                onChange={e => setTuKhoa(e.target.value)}
                placeholder="Tìm mã đơn"
                className="h-6 w-32 rounded-full bg-[#f1f5f9] px-3 text-[13px] outline-none focus:bg-[#eff6ff]"
              />
            </div>
          )}
        </div>

        <div className="max-h-[260px] overflow-auto rounded-md border" style={{ borderColor: C.vien }}>
          <table className="w-full border-separate bg-white" style={{ borderSpacing: 0, tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 44 }} />
              <col style={{ width: 130 }} />
              <col />
              <col style={{ width: 120 }} />
              <col style={{ width: 130 }} />
              <col style={{ width: 110 }} />
            </colgroup>
            <thead>
              <tr>
                <th className={`${TH} text-right`}>#</th>
                <th className={`${TH} text-left`}>Mã đơn</th>
                <th className={`${TH} text-left`}>Loại</th>
                <th className={`${TH} ${NUM}`}>Tiền nợ</th>
                <th className={`${TH} ${NUM}`}>Thanh toán</th>
                <th className={`${TH} ${NUM}`}>Còn nợ</th>
              </tr>
            </thead>

            <tbody>
              {soDon === 0 && (
                <tr>
                  <td colSpan={6} className={`${TD} py-4 text-center`} style={{ color: C.chu2 }}>
                    Chưa chọn đơn nào
                  </td>
                </tr>
              )}

              {/* Đơn trả một phần: dòng đầu, dính dưới header. Viền trái cam để nhận ra, nền vẫn trắng */}
              {phanBo && (
                <tr className="align-top">
                  {(() => {
                    const c = `sticky top-[28px] z-10 bg-white ${TD}`;
                    const vien = { borderBottom: `1px solid ${C.vien}` };
                    return (
                      <>
                        <td
                          className={`${c} ${NUM}`}
                          style={{ ...vien, color: C.chu2, boxShadow: `inset 3px 0 0 ${C.camBadge}` }}
                        >
                          1
                        </td>
                        <td className={`${c} font-medium`} style={vien}>
                          {phanBo.maDonHang}
                        </td>
                        <td className={`${c} whitespace-normal`} style={vien}>
                          <div>
                            {phanBo.loai}
                            <NhanMotPhan />
                          </div>
                          <div className="text-[12px]" style={{ color: C.chu2 }}>
                            Tiền trừ vào:{' '}
                            {THU_NO_EDIT_KEYS.filter(key => phanBo.cot[key] > 0)
                              .map(key => `${TEN_O[key]} ${tien(phanBo.cot[key])}`)
                              .join(', ')}
                          </div>
                        </td>
                        <td className={`${c} ${NUM}`} style={vien}>
                          {tien(phanBo.tongNo)}
                        </td>
                        <td className={`${c} ${NUM}`} style={{ ...vien, color: C.xanhChu }}>
                          {tien(phanBo.tien)}
                        </td>
                        <td className={`${c} ${NUM}`} style={{ ...vien, color: C.cam }}>
                          {tien(phanBo.noConLai)}
                        </td>
                      </>
                    );
                  })()}
                </tr>
              )}

              {data.donThanhToan.length > 0 && dsHienThi.length === 0 && (
                <tr>
                  <td colSpan={6} className={`${TD} py-3 text-center`} style={{ color: C.chu2 }}>
                    Không có đơn khớp bộ lọc.{' '}
                    <button
                      type="button"
                      onClick={xoaLoc}
                      className="font-medium underline"
                      style={{ color: C.xanhChu }}
                    >
                      Xóa lọc
                    </button>
                  </td>
                </tr>
              )}

              {/* Đơn trả đủ: nền trắng, đường kẻ rất nhạt, không nhãn từng dòng (cả khối là "trả đủ") */}
              {dsHienThi.map((d, i) => {
                const vien = { borderBottom: `1px solid ${C.vienNhat}` };
                return (
                  <tr key={d.key} className="hover:bg-[#f8fafc]">
                    <td className={`${TD} ${NUM}`} style={{ ...vien }}>
                      {i + 1 + (phanBo ? 1 : 0)}
                    </td>
                    <td className={`${TD} font-medium`} style={vien}>
                      {d.maDonHang}
                    </td>
                    <td className={`${TD} truncate`} style={{ ...vien, color: C.chu2 }}>
                      {d.loai}
                    </td>
                    <td className={`${TD} ${NUM}`} style={vien}>
                      {tien(d.tienNo)}
                    </td>
                    <td className={`${TD} ${NUM}`} style={vien}>
                      {tien(d.tienNo)}
                    </td>
                    <td className={`${TD} ${NUM}`} style={{ ...vien }}>
                      –
                    </td>
                  </tr>
                );
              })}
            </tbody>

            <tfoot>
              {dangLoc && (
                <tr>
                  <td
                    colSpan={3}
                    className={`${FOOT} bottom-[32px] h-[24px]`}
                    style={{ background: C.xanhNen, color: C.chu2 }}
                  >
                    Đang lọc {dsHienThi.length}/{data.donThanhToan.length} đơn trả đủ
                    <button
                      type="button"
                      onClick={xoaLoc}
                      className="ml-3 font-medium underline"
                      style={{ color: C.xanhChu }}
                    >
                      Xóa lọc
                    </button>
                  </td>
                  {[tongHienThi, tongHienThi, 0].map((v, i) => (
                    <td
                      key={i}
                      className={`${FOOT} bottom-[32px] h-[24px] ${NUM}`}
                      style={{ background: C.xanhNen, color: i === 2 ? C.chu2 : C.xanhChu }}
                    >
                      {tien(v)}
                    </td>
                  ))}
                </tr>
              )}
              <tr>
                <td
                  colSpan={3}
                  className={`${FOOT} bottom-0 h-[32px] font-semibold`}
                  style={{ background: C.nen, color: C.ink, borderTop: VIEN_TONG }}
                >
                  Tổng cộng
                </td>
                <td
                  className={`${FOOT} bottom-0 h-[32px] ${NUM} font-semibold`}
                  style={{ background: C.nen, color: C.ink, borderTop: VIEN_TONG }}
                >
                  {tien(congTienNo)}
                </td>
                <td
                  className={`${FOOT} bottom-0 h-[32px] ${NUM} font-semibold`}
                  style={{ background: C.nen, color: C.ink, borderTop: VIEN_TONG }}
                >
                  {tien(tongGachNo)}
                </td>
                <td
                  className={`${FOOT} bottom-0 h-[32px] ${NUM} font-semibold`}
                  style={{
                    background: C.nen,
                    color: congConNo > 0 ? C.cam : C.ink,
                    borderTop: VIEN_TONG
                  }}
                >
                  {tien(congConNo)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      {/* ===== CÂU CHỐT: đọc lại hành động sắp thực hiện ngay trước khi bấm Xác nhận ===== */}
      {/* <div
        className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 rounded-md border px-4 py-2.5"
        style={{
          background: C.xanhNen,
          borderColor: '#bfdbfe',
          borderLeft: `4px solid ${C.xanhChu}`
        }}
      >
        <div className="min-w-0">
          <div style={{ color: C.chu2 }}>Xác nhận tiền đơn</div>
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-[20px] font-bold leading-tight tabular-nums" style={{ color: C.xanhChu }}>
              {tien(tongGachNo)} đ
            </span>

            {phanBo && <span style={{ color: C.chu2 }}>({data.donThanhToan.length} trả đủ, 1 trả một phần)</span>}
          </div>
        </div>

        <div className="text-right">
          <div style={{ color: C.chu2 }}>Tiền dư</div>
          <div
            className="text-[20px] font-bold leading-tight tabular-nums"
            style={{ color: data.duNoConLai > 0 ? C.cam : C.luc }}
          >
            {tien(data.duNoConLai)} đ
          </div>
        </div>
      </div> */}
    </div>
  );
};
